// Imperative, framework-agnostic controller for the StudiAssist chat launcher.
//
// This is a parameterized port of the production widget that ships to TYPO3
// customers (website/public/assets/chatbot-widget.js): same fixed bottom-right
// round button, online-pulse dot, teaser bubble, pop-in panel, ESC-to-close,
// focus handling, responsive breakpoints and reduced-motion support.
//
// Differences from the TYPO3 script:
//   * No speech-bubble icon (configurable: sparkle / cap / robot).
//   * Colors use HSH brand CSS variables defined in _studiassist.scss.
//   * Chat URL is configurable (supports a `{lang}` placeholder).
//   * It is a SINGLETON with reference counting, so the React layer can mount
//     it from a Scrivito widget on any number of pages without ever creating
//     two launchers. Everything lives in a single `#studiassist-root` container
//     appended to <body>, so it floats above the page regardless of where in
//     the content tree the Scrivito widget was placed.

const getIcon = () => `<i class="las la-robot fs-1" />`;
const CLOSE_ICON = '<i class="las la-times fs-3" />';
const PANEL_CLOSE_ICON = '<i class="las la-times fs-3" />';
const ROOT_ID = "studiassist-root";

const I18N = {
  de: {
    open: "StudiAssist Chat öffnen",
    close: "StudiAssist Chat schließen",
    window: "StudiAssist Chatfenster",
    dismiss: "Schließen",
  },
  en: {
    open: "Open StudiAssist Chat",
    close: "Close StudiAssist Chat",
    window: "StudiAssist Chat Window",
    dismiss: "Dismiss",
  },
};

// ---- Singleton state -------------------------------------------------------

let refCount = 0;
let els = null; // built DOM references
let pendingConfig = null; // config supplied before the DOM was built
const state = {
  open: false,
  lang: "de",
  iconStyle: "sparkle",
  chatUrl: "",
  resolvedUrl: "",
  teaserText: "",
  teaserShown: false,
  teaserDismissedForVisit: false,
};

// ---- Helpers ---------------------------------------------------------------

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function resolveLang(language) {
  if (language === "de" || language === "en") return language;
  const nav =
    (typeof window.navigator !== "undefined" &&
      (window.navigator.language || window.navigator.userLanguage)) ||
    "de";
  return String(nav).toLowerCase().startsWith("en") ? "en" : "de";
}

function resolveUrl(template, lang) {
  if (!template) return "";
  return template.indexOf("{lang}") !== -1
    ? template.replace(/\{lang\}/g, lang)
    : template;
}

function t(key) {
  return (I18N[state.lang] || I18N.de)[key];
}

// ---- Behavior --------------------------------------------------------------

function hideTeaser(dismissForVisit) {
  if (!els || !state.teaserShown) return;
  els.teaser.setAttribute("data-visible", "false");
  setTimeout(() => {
    if (els) els.teaser.style.display = "none";
  }, 220);
  state.teaserShown = false;
  if (dismissForVisit) state.teaserDismissedForVisit = true;
}

function showTeaser() {
  if (!els || !state.teaserText) return;
  if (state.teaserDismissedForVisit || state.open || state.teaserShown) return;
  els.teaser.style.display = "block";
  state.teaserShown = true;
  window.requestAnimationFrame(() => {
    if (els) els.teaser.setAttribute("data-visible", "true");
  });
}

function setOpen(open) {
  if (!els) return;
  state.open = open;
  els.wrapper.style.display = open ? "block" : "none";
  els.button.setAttribute("aria-expanded", open ? "true" : "false");
  els.button.setAttribute("data-open", open ? "true" : "false");
  els.button.setAttribute("aria-label", open ? t("close") : t("open"));
  els.button.title = open ? t("close") : t("open");
  els.button.innerHTML = open ? CLOSE_ICON : getIcon(state.iconStyle);
  if (open) {
    hideTeaser(false);
  } else {
    els.button.focus();
    setTimeout(showTeaser, 450);
  }
}

function toggleChat() {
  setOpen(!state.open);
}

function closeChat() {
  if (!state.open) return;
  setOpen(false);
}

// ---- Build / apply / destroy ----------------------------------------------

function build() {
  const root = document.createElement("div");
  root.id = ROOT_ID;

  const button = document.createElement("button");
  button.id = "studiassist-launcher";
  button.setAttribute("type", "button");
  button.setAttribute("aria-haspopup", "dialog");
  button.setAttribute("aria-expanded", "false");

  const wrapper = document.createElement("div");
  wrapper.id = "studiassist-chatbox";
  wrapper.setAttribute("role", "dialog");
  wrapper.setAttribute("aria-modal", "true");

  const iframe = document.createElement("iframe");
  iframe.id = "studiassist-iframe";
  iframe.setAttribute("tabindex", "0");

  const closeBtn = document.createElement("button");
  closeBtn.id = "studiassist-close";
  closeBtn.setAttribute("type", "button");
  closeBtn.innerHTML = PANEL_CLOSE_ICON;

  wrapper.appendChild(iframe);
  wrapper.appendChild(closeBtn);

  const teaser = document.createElement("div");
  teaser.id = "studiassist-teaser";
  teaser.setAttribute("role", "button");
  teaser.setAttribute("tabindex", "0");

  root.appendChild(wrapper);
  root.appendChild(teaser);
  root.appendChild(button);
  document.body.appendChild(root);

  els = { root, button, wrapper, iframe, closeBtn, teaser };

  // Wire up events once.
  button.addEventListener("click", () => toggleChat());
  button.addEventListener("keydown", (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggleChat();
    }
  });
  closeBtn.addEventListener("click", closeChat);
  els.onKeydown = (e) => {
    if (state.open && e.key === "Escape") closeChat();
  };
  window.addEventListener("keydown", els.onKeydown);

  teaser.addEventListener("click", (e) => {
    if (e.target && e.target.id === "studiassist-teaser-close") return;
    hideTeaser(true);
    if (!state.open) toggleChat();
  });
  teaser.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      hideTeaser(true);
      if (!state.open) toggleChat();
    }
  });
}

function applyConfig(config) {
  if (!config) return;
  state.lang = resolveLang(config.language);
  state.iconStyle = config.iconStyle || "sparkle";
  state.chatUrl = config.chatUrl || "";
  state.teaserText = config.teaserText || "";

  if (!els) return;

  // Online status dot toggle.
  els.button.setAttribute("data-status", config.showStatusDot ? "on" : "off");

  // Labels (depend on resolved language).
  els.button.setAttribute("aria-label", state.open ? t("close") : t("open"));
  els.button.title = state.open ? t("close") : t("open");
  els.wrapper.setAttribute("aria-label", t("window"));
  els.iframe.setAttribute("title", t("window"));

  // Icon — only swap when closed (open state shows the close glyph).
  if (!state.open) els.button.innerHTML = getIcon(state.iconStyle);

  // Chat URL — only (re)assign when it actually changes to avoid reloading
  // the iframe on every re-render.
  const nextUrl = resolveUrl(state.chatUrl, state.lang);
  if (nextUrl && nextUrl !== state.resolvedUrl) {
    state.resolvedUrl = nextUrl;
    els.iframe.src = nextUrl;
  }

  // Teaser content + visibility.
  if (state.teaserText) {
    els.teaser.setAttribute("aria-label", state.teaserText);
    els.teaser.innerHTML = `
      <div id="studiassist-teaser-content">
        <span></span>
        <button id="studiassist-teaser-close" type="button" aria-label="${t(
          "dismiss"
        )}">×</button>
      </div>
    `;
    // Set text via textContent to avoid HTML injection from the attribute.
    els.teaser.querySelector("#studiassist-teaser-content span").textContent =
      state.teaserText;
    const teaserCloseEl = els.teaser.querySelector("#studiassist-teaser-close");
    if (teaserCloseEl) {
      teaserCloseEl.addEventListener("click", (e) => {
        e.stopPropagation();
        hideTeaser(true);
      });
    }
    window.requestAnimationFrame(showTeaser);
  } else {
    hideTeaser(false);
  }
}

function destroy() {
  if (!els) return;
  window.removeEventListener("keydown", els.onKeydown);
  if (els.root && els.root.parentNode) {
    els.root.parentNode.removeChild(els.root);
  }
  els = null;
  state.open = false;
  state.resolvedUrl = "";
  state.teaserShown = false;
  state.teaserDismissedForVisit = false;
}

// ---- Public API (used by the Scrivito component) ---------------------------

export function mountLauncher() {
  if (!isBrowser()) return;
  refCount += 1;
  if (!els) {
    build();
    if (pendingConfig) {
      applyConfig(pendingConfig);
      pendingConfig = null;
    }
  }
}

export function updateLauncher(config) {
  if (!isBrowser()) return;
  if (!els) {
    pendingConfig = config;
    return;
  }
  applyConfig(config);
}

export function unmountLauncher() {
  if (!isBrowser()) return;
  refCount = Math.max(0, refCount - 1);
  if (refCount === 0) destroy();
}
